import { z } from 'zod';
/**
 * Live copilot API contracts (COS-109): the authenticated endpoints the cosow-live desktop
 * app calls so it needs no client-side Gemini key.
 *
 *   POST /ai/live/transcribe   audio chunk -> text, using the workspace's own AI config and key
 *   POST /ai/live/suggest      transcript so far -> ranked questions to ask + answers heard
 *   POST /ai/live/ask          one question or short task about the call -> answer + citations
 *
 * All sit behind the normal sales auth (Firebase ID token or machine token, workspace resolved
 * from the host). The spec is specs/COS-109-live-api.md.
 */
// ── Mechanics (hardcoded, not workspace policy) ───────────────────────────────
/** Largest decoded audio chunk /ai/live/transcribe accepts (2 MB). */
export const LIVE_TRANSCRIBE_MAX_AUDIO_BYTES = 2 * 1024 * 1024;
/** Longest audio chunk /ai/live/transcribe accepts, in milliseconds (30 s). */
export const LIVE_TRANSCRIBE_MAX_DURATION_MS = 30_000;
/** Base64 length of the largest accepted chunk (4 chars per 3 bytes, padded). */
export const LIVE_TRANSCRIBE_MAX_BASE64_CHARS = Math.ceil(LIVE_TRANSCRIBE_MAX_AUDIO_BYTES / 3) * 4;
/** Longest transcript /ai/live/suggest accepts; the server then keeps only the tail it uses. */
export const LIVE_SUGGEST_MAX_TRANSCRIPT_CHARS = 60_000;
// ── POST /ai/live/transcribe ──────────────────────────────────────────────────
export const liveAudioMimeTypeSchema = z.enum(['audio/webm;codecs=opus', 'audio/wav']);
export const liveTranscribeRequestSchema = z.object({
    /** Standard base64 of one audio chunk. Decoded size is capped at LIVE_TRANSCRIBE_MAX_AUDIO_BYTES. */
    audioBase64: z.string().min(1).max(LIVE_TRANSCRIBE_MAX_BASE64_CHARS),
    mimeType: liveAudioMimeTypeSchema,
    /** Optional BCP-47 language tag, e.g. "en" or "en-US". A hint to the model, not a constraint. */
    languageHint: z
        .string()
        .regex(/^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/, 'languageHint must be a BCP-47 tag')
        .max(35)
        .optional(),
    /** Client-chosen id for one live session. Groups a call's chunks in logs. */
    sessionId: z.string().min(1).max(128),
});
export const liveTranscribeResponseSchema = z.object({
    /** Transcript of the chunk. Empty string when the chunk holds no speech. */
    text: z.string(),
    /** Wall-clock time the server spent on the call, in milliseconds (not the audio length). */
    durationMs: z.number().int().nonnegative(),
});
// ── POST /ai/live/suggest ─────────────────────────────────────────────────────
/**
 * Which deal template's field list the questions come from. `netsuite_existing` is the
 * "Existing NetSuite" category (continuous improvement) of the NetSuite product.
 */
export const liveProductSchema = z.enum(['maxio', 'netsuite_existing', 'netsuite_greenfield']);
/** Most client-defined fields one /ai/live/suggest call may carry. */
export const LIVE_SUGGEST_MAX_CLIENT_FIELDS = 40;
export const liveClientFieldTypeSchema = z.enum(['text', 'number', 'select', 'multi_select', 'boolean']);
/**
 * A question the CLIENT asks on the call that is not (or not only) a field of the workspace deal
 * template, e.g. the desktop app's Existing NetSuite scoping prompts. The server extracts the
 * prospect's answer for it and ranks it like any other open field; the captured value comes back
 * under the same `key`. A key that is also a template field key is ignored here: the template
 * field wins.
 */
export const liveClientFieldSchema = z.object({
    key: z.string().min(1).max(64),
    /** What the question asks, in the rep's words. The extractor reads it as the field label. */
    label: z.string().min(1).max(200),
    type: liveClientFieldTypeSchema,
    /** For select / multi_select: the allowed answers. The captured value is an option `value`. */
    options: z
        .array(z.object({ value: z.string().min(1).max(200), label: z.string().min(1).max(200) }))
        .max(50)
        .optional(),
    /** Group label for the ranking and the "why" line. Defaults to "Client questions". */
    section: z.string().min(1).max(100).optional(),
    /** Extra guidance for the extractor about what counts as an answer. */
    hint: z.string().min(1).max(500).optional(),
});
export const liveSuggestRequestSchema = z.object({
    product: liveProductSchema,
    /** The call so far. One line per utterance, "Speaker: text" when speakers are known. */
    transcriptSoFar: z.string().max(LIVE_SUGGEST_MAX_TRANSCRIPT_CHARS),
    /** Keys of questions the rep already asked or dismissed. Never suggested again. */
    askedKeys: z.array(z.string().min(1).max(200)).max(500).default([]),
    /** When set, fields already answered on this deal are left out. Same access as the deal itself. */
    dealUuid: z.string().uuid().nullable().optional(),
    /** Optional id of the live session. Rate limiting is per session; without it, per user. */
    sessionId: z.string().min(1).max(128).optional(),
    /**
     * Extra questions the client asks, extracted and ranked next to the template's own fields.
     * Optional: without it the call behaves exactly as before.
     */
    fields: z.array(liveClientFieldSchema).max(LIVE_SUGGEST_MAX_CLIENT_FIELDS).optional(),
});
export const liveSuggestQuestionSchema = z.object({
    /** Template field key. Send it back in `askedKeys` once the rep asks or dismisses it. */
    key: z.string(),
    text: z.string(),
    /** Rank, 1 = ask first. Set by the server: required + hours-moving fields first. */
    priority: z.number().int().min(1),
    /** The template marks this field required. */
    required: z.boolean(),
    /** One line saying why this field matters now. */
    reason: z.string(),
    /**
     * Top question only: a short spoken acknowledgement of what the prospect just said (at most
     * LIVE_SUGGEST_MAX_BRIDGE_WORDS words). Empty string when there is nothing new to acknowledge.
     */
    bridge: z.string().optional(),
    /** Top question only: one-sentence recap of what was captured, set only on a topic change; else null. */
    recap: z.string().nullable().optional(),
});
/** Longest `bridge` on the top question, in words. */
export const LIVE_SUGGEST_MAX_BRIDGE_WORDS = 12;
/** Longest `evidence` string on a captured entry. */
export const LIVE_SUGGEST_MAX_EVIDENCE_CHARS = 240;
export const liveSuggestCapturedStatusSchema = z.enum(['answered', 'unknown']);
export const liveSuggestCapturedSchema = z.object({
    key: z.string(),
    /**
     * The answer as the questionnaire stores it (a string; currency answers are a JSON string).
     * null when `status` is "unknown": the prospect said they do not know or will confirm later.
     */
    value: z.string().nullable(),
    /** 0..1 when the model scores the answer; null when it does not (the extractor does not today). */
    confidence: z.number().min(0).max(1).nullable(),
    /** The prospect's own words the value (or the "don't know") came from, at most 240 characters. */
    evidence: z.string().max(LIVE_SUGGEST_MAX_EVIDENCE_CHARS).optional(),
    /**
     * "answered" (the default when absent) or "unknown". An unknown entry carries `value: null` so the
     * client can mark the field handled and stop asking it; a later answer for the same key replaces it.
     */
    status: liveSuggestCapturedStatusSchema.optional(),
});
export const liveSuggestResponseSchema = z.object({
    questions: z.array(liveSuggestQuestionSchema),
    captured: z.array(liveSuggestCapturedSchema),
});
// ── POST /ai/live/ask ─────────────────────────────────────────────────────────
/** Most transcript turns one /ai/live/ask call may carry. The server then keeps the newest words. */
export const LIVE_ASK_MAX_TURNS = 2_000;
/** Longest single transcript turn, in characters. */
export const LIVE_ASK_MAX_TURN_CHARS = 5_000;
/** Longest question or task, in characters. */
export const LIVE_ASK_MAX_QUESTION_CHARS = 1_000;
/** Most captured answers and open questions one call may carry. */
export const LIVE_ASK_MAX_CAPTURED = 200;
export const LIVE_ASK_MAX_OPEN_QUESTIONS = 100;
/** Most citations in a reply. */
export const LIVE_ASK_MAX_CITATIONS = 3;
export const liveAskTurnSchema = z.object({
    /** Who spoke, as the client labels it ("SA", "Prospect", "you", "them", or a name). */
    speaker: z.string().trim().min(1).max(100),
    text: z.string().max(LIVE_ASK_MAX_TURN_CHARS),
    /** The client's timestamp for the turn. Echoed back unchanged in `citations[].t`. */
    t: z.number().finite(),
});
export const liveAskRequestSchema = z.object({
    /** The rep's question about the call, or a short task ("draft the follow-up email", "summarise so far"). */
    question: z.string().trim().min(1).max(LIVE_ASK_MAX_QUESTION_CHARS),
    /** The call so far, oldest first. */
    transcript: z.array(liveAskTurnSchema).max(LIVE_ASK_MAX_TURNS),
    /** Answers captured so far (the client's field list). */
    captured: z
        .array(z.object({
        key: z.string().min(1).max(200),
        label: z.string().min(1).max(300),
        value: z.string().max(2_000),
    }))
        .max(LIVE_ASK_MAX_CAPTURED)
        .default([]),
    /** Questions still open, most important first. */
    openQuestions: z.array(z.string().min(1).max(500)).max(LIVE_ASK_MAX_OPEN_QUESTIONS).default([]),
    /** The prospect company's name, when known. */
    customer: z.string().trim().max(200).optional(),
});
export const liveAskCitationSchema = z.object({
    /** The cited turn's `t`, exactly as the client sent it. */
    t: z.number(),
    /** The cited turn's text, at most 140 characters. */
    text: z.string(),
});
export const liveAskResponseSchema = z.object({
    /** Plain text. Says so plainly when the call does not cover the question. */
    answer: z.string(),
    /** Up to 3 transcript turns that support the answer. Empty when nothing in the call does. */
    citations: z.array(liveAskCitationSchema).max(LIVE_ASK_MAX_CITATIONS),
    /** Wall-clock time the server spent on the call, in milliseconds. */
    latencyMs: z.number().int().nonnegative(),
});
//# sourceMappingURL=live-api.js.map